import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import styles from "../NewsModal/styles/NewsModal.module.scss";
import accordionStyles from "./styles/AccordionItemModal.module.scss";
import NewsModalContentHeader from "../NewsModal/NewsModalContentHeader";
import classNames from "classnames";
import { buildAccordionItemUrl } from "../../../utils/SchoolBlocksUtilities";
import SchoolBlocksLoadingIndicator from "../../utilities/SchoolBlocksLoadingIndicator";
import Markdown from "../../utilities/Markdown";
import { useStores } from "../../../stores/useStores";
import { EditButton } from "../../blocks/TerracedGridTheme/EditButton";
import dynamic from "next/dynamic";
import EditableField from "../../utilities/EditableField";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";

const AccordionItemSummaryContentEditor = dynamic(
  () =>
    import("../../blocks/TerracedGridTheme/AccordionTerrace/AccordionItemSummaryContentEditor"),
);

const AccordionItemModalContent = observer(
  (props: {
    index: number;
    itemObj: IAccordionItem;
    setItemObj: (itemObj: IAccordionItem) => void;
    handleSave: (item: IAccordionItemEditing) => Promise<IAccordionItem>;
    navigationButtons?: React.ReactElement;
    closeFunction?: () => void;
    className?: string;
    blockObj: IAccordionTerrace;
  }) => {
    const { organizationStore, userStore } = useStores();

    const [editing, setEditing] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);
    const sharePathUrl = buildAccordionItemUrl(
      organizationStore.currentOrganization.id,
      props.itemObj.terrace_id,
      props.itemObj.id,
    );

    const titleContainerClassName = classNames({
      [styles.titleContainer]: true,
      [accordionStyles.accordionTitle]: true,
    });

    const containerClassName = classNames({
      [styles.container]: true,
      [accordionStyles.accordionContainer]: true,
      [props.className || ""]: props.className,
    });

    let content;
    if (
      organizationStore.organization.billing_products?.docmersion &&
      props.itemObj.has_content &&
      props.itemObj.modal_enabled
    ) {
      content = (
        <>
          {props.itemObj.content_html ? (
            <div className={styles.contentContainer}>
              <div
                className={classNames(styles.content, "sb-scoped-html-base")}
              >
                <Markdown
                  source={props.itemObj.content_html}
                  withStyles={true}
                />
              </div>
            </div>
          ) : (
            <div className={styles.loadingIndicator}>
              <SchoolBlocksLoadingIndicator />
            </div>
          )}
        </>
      );
    } else if (props.itemObj.description) {
      content = (
        <div className={classNames(styles.content)}>
          <Markdown withStyles={true} source={props.itemObj.description} />
        </div>
      );
    }

    async function handleSaveTitle(newTitle: string) {
      if (newTitle.length == 0) {
        NotificationManager.error("A title is required.");
        return;
      }
      if (newTitle !== props.itemObj.title) {
        try {
          const client = await WatsonApi();
          const response =
            await client.apis.organizations.organizations_terraces_accordion_items_partial_update(
              {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                id: (props.itemObj as IAccordionItem).id,
                data: {
                  title: newTitle,
                },
              },
            );
          NotificationManager.success("Title successfully saved!");
          props.setItemObj({ ...props.itemObj, title: newTitle });
          setEditingTitle(false);

          const updatedItem = JSON.parse(response.data);

          props.blockObj.items = (props.blockObj.items as IAccordionItem[]).map(
            (item) => {
              return item.id === (props.itemObj as IAccordionItem).id
                ? updatedItem
                : item;
            },
          );
        } catch {
          NotificationManager.error("Something went wrong! Please try again.");
        }
      } else {
        setEditingTitle(false);
      }
    }

    return (
      <div ref={contentRef} className={containerClassName}>
        <NewsModalContentHeader
          index={props.index}
          itemObj={props.itemObj}
          closeFunction={props.closeFunction}
          navigationButtons={props.navigationButtons}
          shareUrlPath={sharePathUrl}
          printRef={contentRef}
        />
        <article>
          <div className={titleContainerClassName}>
            <EditableField
              defaultValue={props.itemObj.title}
              isEditing={editingTitle}
              onEditStart={() => {
                setEditingTitle(true);
              }}
              onSave={handleSaveTitle}
              onCancel={() => {
                setEditingTitle(false);
              }}
              canEdit={userStore.isEditor}
              placeholder="Enter a Title"
              className={styles.editableTitle}
            >
              <h2>{props.itemObj.title}</h2>
            </EditableField>
            {userStore.isEditor && (
              <EditButton
                onClick={() => {
                  setEditing((v) => !v);
                }}
                contentItem={props.itemObj}
                handleDelete={() => {}}
              />
            )}
          </div>
          {editing ? (
            <AccordionItemSummaryContentEditor
              blockObj={props.blockObj}
              item={props.itemObj}
              index={props.index}
              showContentEditor={true}
              handleSave={props.handleSave}
              handleCancel={() => {
                setEditing(false);
              }}
              className={styles.accordionEditing}
            />
          ) : (
            content
          )}
        </article>
      </div>
    );
  },
);

export default AccordionItemModalContent;
